import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { captureClickIds, getAttribution } from "@/lib/analytics/clickIds";
import { pushAttribution } from "@/lib/analytics/dataLayer";
import { DISPUTE_PROTECTION_ENABLED } from "@/lib/booking/featureFlags";
import { clearPendingCapture } from "@/lib/booking/pendingCapture";

// Never let a stale pre-disable waiver retry record survive into checkout.
if (!DISPUTE_PROTECTION_ENABLED) clearPendingCapture();

// Capture paid-attribution (gclid, fbclid, _fbp, _fbc, UTMs, ...) once at boot,
// before React renders, so downstream analytics + booking POSTs include it.
captureClickIds();

// Expose attribution on the dataLayer so GTM-fired tags (GA4, Meta, Google Ads)
// can include click IDs on every downstream event.
{
  const a = getAttribution();
  pushAttribution({
    gclid: a.gclid ?? undefined,
    gbraid: a.gbraid ?? undefined,
    wbraid: a.wbraid ?? undefined,
    fbclid: a.fbclid ?? undefined,
    fbp: a.fbp ?? undefined,
    fbc: a.fbc ?? undefined,
    msclkid: a.msclkid ?? undefined,
  });
}

// Preload critical hero image for LCP optimization
import heroImage from "@/assets/hero-riverfront-cabins.webp";
const preloadLink = document.createElement('link');
preloadLink.rel = 'preload';
preloadLink.as = 'image';
preloadLink.href = heroImage;
preloadLink.fetchPriority = 'high';
document.head.appendChild(preloadLink);

// Register service worker for caching and offline support
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  // A tab that was already open keeps running its old JS bundle after a
  // deploy. The SW posts SW_UPDATED once when a newer build activates —
  // reload a single time so guests never transact on a stale bundle.
  let reloadedForUpdate = false;
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type !== 'SW_UPDATED' || reloadedForUpdate) return;
    reloadedForUpdate = true;
    window.location.reload();
  });
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('SW registered:', registration.scope);
      })
      .catch((error) => {
        console.log('SW registration failed:', error);
      });
  });
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);